<template>
  <Card
    :id="id"
    class="arrangeable-location-card"
    :class="{
      'not-draggable': isStopResolved,
      'not-droppable': resequenceError,
    }"
  >
    <template #content>
      <div class="arrangeable-location-card-cell w-4">
        <FontAwesomeIcon
          aria-hidden="true"
          class="drag-handle"
          :icon="['fas', 'grid-round']"
        />
        <FontAwesomeIcon
          :icon="['fas', 'map-marker-alt']"
          aria-hidden="true"
          :class="isPickup ? 'text-pickup' : 'text-dropoff'"
        />
        <div>
          <div>{{ stop.location.cityState }}</div>
          <div>
            {{ isPickup ? t('shipments.picks') : t('shipments.drops') }}
          </div>
        </div>
      </div>

      <div v-if="resequenceError" class="arrangeable-location-card-cell w-7">
        <div class="cant-resequence-reason">
          <LabeledIcon
            icon="pi pi-exclamation-circle"
            :label="resequenceError"
          />
        </div>
      </div>
      <div v-else class="arrangeable-location-card-cell w-4">
        <LineItemSummary :line-items="lineItems" show-handling-units />
      </div>

      <div v-if="!resequenceError" class="arrangeable-location-card-cell w-3">
        <LocationCardDate
          :label="t('events.appointmentDateTime')"
          :date="stop.requestedAppointmentEarliest"
          use-placeholder
        />
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { computed, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { ShipmentStopType } from '@/model';

import LineItemSummary from '@/components/lineItem/LineItemSummary.vue';
import LocationCardDate from '@/components/location/LocationCardDate.vue';
import {
  ArrangeableShipmentLineItem,
  ArrangeableShipmentStop,
} from '@/components/multistop/ArrangeableShipmentStopList.vue';
import LabeledIcon from '@/components/ui/LabeledIcon.vue';
import Card from 'primevue/card';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  stop: {
    type: Object as PropType<ArrangeableShipmentStop>,
    required: true,
  },
  lineItems: {
    type: Array as PropType<ArrangeableShipmentLineItem[]>,
    default: () => [],
  },
  resequenceError: {
    type: String,
    default: () => '',
  },
  requireBothDates: {
    type: Boolean,
    default: () => false,
  },
});
const { id, stop, lineItems, resequenceError, requireBothDates } =
  toRefs(props);

const { t } = useI18n();

const isStopResolved = computed(() => {
  return stop.value.isResolved(requireBothDates.value);
});

const isPickup = computed(
  () => stop.value.stopType === ShipmentStopType.PICKUP,
);
</script>

<style>
:root {
  --arrangeable-location-card-border-color: transparent;
  --arrangeable-location-card-cursor: grab;
  --arrangeable-location-card-drag-handle-color: var(--p-surface-400);
}

.arrangeable-location-card.p-card {
  --p-card-body-padding: var(--sp-3);

  border: 1px solid var(--arrangeable-location-card-border-color);
  cursor: var(--arrangeable-location-card-cursor);
  width: 100%;

  &.not-draggable {
    --arrangeable-location-card-drag-handle-color: var(--p-surface-300);
    --arrangeable-location-card-border-color: var(--p-surface-300);
    --arrangeable-location-card-cursor: not-allowed;
    --p-card-shadow: none;
  }

  &.not-droppable {
    --arrangeable-location-card-border-color: var(--danger-color);
    --arrangeable-location-card-drag-handle-color: var(--p-surface-300);
    --p-card-shadow: none;

    .pi {
      color: var(--danger-color);
    }
  }

  .p-card-body {
    .p-card-content {
      display: flex;

      .arrangeable-location-card-cell {
        display: flex;
        gap: var(--gap-1);
        align-items: center;
        flex: 1 0 auto;

        &:not(:last-of-type) {
          border-right: var(--divider);
          margin-right: var(--sp-3);
          padding-right: var(--sp-3);
        }

        .drag-handle {
          color: var(--arrangeable-location-card-drag-handle-color);
        }

        .key-value-pair-value {
          display: flex;
          flex-direction: column;
          gap: var(--sp-1);
        }

        .cant-resequence-reason {
          display: flex;
          flex-flow: column;
          justify-content: center;
          /* Same height as the date cell to keep the error card the same size */
          height: 81px;
        }
      }
    }
  }
}
</style>
